// Symbol definitions (docs/05 §1). Effects are tiny pure hooks; the engine lives in sim/effects.ts.
import { B } from './balance';
import { defineSymbol, isLike, type GridView, type SymbolDef } from './defs';
import type { Cell } from '../sim/types';

// ── Classic and fruit (§1.2) ───────────────────────────────────────────
const classic: SymbolDef[] = [
  defineSymbol({ id: 'cherry', tags: ['fruit'], rarity: 'C', baseValue: 1, gate: 'start', rotsInto: 'rotten_cherry' }),
  defineSymbol({ id: 'lemon', tags: ['fruit'], rarity: 'C', baseValue: 1, gate: 'start', rotsInto: 'rotten_lemon' }),
  defineSymbol({ id: 'plum', tags: ['fruit'], rarity: 'C', baseValue: 2, gate: 'start', rotsInto: 'rotten_plum' }),
  defineSymbol({
    id: 'grape',
    tags: ['fruit'],
    rarity: 'U',
    baseValue: 1,
    gate: 'start',
    rotsInto: 'rotten_grape',
    add: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) if (n.def.id === 'grape') c.add(c.self.cell, 1);
    },
  }),
  defineSymbol({ id: 'melon', tags: ['fruit'], rarity: 'R', baseValue: 6, gate: 'start', rotsInto: 'rotten_melon' }),
  defineSymbol({ id: 'bell', tags: ['classic'], rarity: 'C', baseValue: 2, gate: 'start' }),
  defineSymbol({ id: 'bar', tags: ['classic', 'bar'], rarity: 'U', baseValue: 3, gate: 'start' }),
  defineSymbol({ id: 'bar2', tags: ['classic', 'bar'], rarity: 'R', baseValue: 6, gate: 'start' }),
  defineSymbol({ id: 'bar3', tags: ['classic', 'bar'], rarity: 'R', baseValue: 10, gate: 'start' }),
  defineSymbol({ id: 'seven', tags: ['classic'], rarity: 'R', baseValue: 7, gate: 'start' }),
  defineSymbol({ id: 'clover', tags: ['classic'], rarity: 'U', baseValue: 2, gate: 'start', luck: 1 }),
  defineSymbol({ id: 'diamond', tags: ['classic'], rarity: 'R', baseValue: 8, gate: 'start' }),
  defineSymbol({
    id: 'horseshoe',
    tags: ['classic'],
    rarity: 'U',
    baseValue: 1,
    gate: 'start',
    add: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) c.add(n.cell, 1);
    },
  }),
  defineSymbol({ id: 'coin', tags: ['classic'], rarity: 'C', baseValue: 1, gate: 'start', offPattern: true }),
  defineSymbol({
    id: 'moneybag',
    tags: ['classic'],
    rarity: 'U',
    baseValue: 4,
    gate: 'start',
    offPattern: true,
  }),
  defineSymbol({ id: 'dice', tags: ['classic'], rarity: 'U', baseValue: 0, gate: 'start', roll: [1, 6] }),
  defineSymbol({
    id: 'pearl',
    tags: ['classic'],
    rarity: 'R',
    baseValue: 9,
    gate: 'start',
    add: (c) => {
      if (!c.grid.all().some((v) => v.def.tags.includes('fruit'))) c.add(c.self.cell, 3);
    },
  }),
  defineSymbol({ id: 'grin', tags: ['wild'], rarity: 'R', baseValue: 0, gate: 'start', wild: true }),
  defineSymbol({ id: 'crown', tags: ['classic'], rarity: 'L', baseValue: 25, gate: 'start', patternX: 2 }),
];

// ── Machine-awake symbols (§1.3, after G_GROW_1) ───────────────────────
const nearCandle = (grid: GridView, cell: Cell): boolean =>
  grid.neighbors(cell).some((n) => isLike(n.def, 'candle'));

const awake: SymbolDef[] = [
  defineSymbol({
    id: 'heart',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 5,
    gate: 'awake',
    land: (c) => {
      const inst = c.self.inst;
      if (inst.counters.lastWindow !== c.windowN) {
        inst.counters.lastWindow = c.windowN;
        c.grow(inst, 1);
      }
    },
    post: (c) => {
      if (c.inPayingPattern) c.hungerLater(-3);
    },
  }),
  defineSymbol({
    id: 'candle',
    tags: ['light'],
    rarity: 'U',
    baseValue: 3,
    gate: 'awake',
    land: (c) => {
      if (c.mods.candleNoMelt) return;
      if (c.mods.zippo && c.grid.neighbors(c.self.cell).some((n) => isLike(n.def, 'moth'))) return;
      if (c.self.inst.age >= B.CANDLE_MELT) c.transform(c.self.inst, 'wax');
    },
    add: (c) => {
      const matches = c.grid.neighbors(c.self.cell).filter((n) => n.def.id === 'match').length;
      const aura = matches > 0 ? 2 : 1;
      for (const n of c.grid.neighbors(c.self.cell)) c.add(n.cell, aura);
    },
  }),
  defineSymbol({
    id: 'wax',
    tags: ['light'],
    rarity: null,
    baseValue: 0,
    gate: 'awake',
    land: (c) => {
      if (c.self.inst.age >= B.WAX_RELIGHT) c.transform(c.self.inst, 'candle');
    },
  }),
  defineSymbol({
    id: 'moth',
    tags: ['vermin'],
    rarity: 'C',
    baseValue: 1,
    gate: 'awake',
    land: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) if (n.def.id === 'candle') c.ageBy(n.inst, 1);
    },
    mul: (c) => {
      if (c.mods.mothLamp || nearCandle(c.grid, c.self.cell)) c.mul(c.self.cell, 3);
    },
  }),
  defineSymbol({
    id: 'lantern',
    tags: ['light'],
    rarity: 'R',
    baseValue: 4,
    gate: 'awake',
    countsAs: ['candle'],
    add: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) c.add(n.cell, 1);
    },
  }),
  defineSymbol({
    id: 'match',
    tags: ['light'],
    rarity: 'C',
    baseValue: 1,
    gate: 'awake',
    mul: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) if (n.def.id === 'candle') c.mul(n.cell, 2);
      c.destroyLater(c.self.inst.uid, 'match');
    },
  }),
  defineSymbol({
    id: 'chalice',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 7,
    gate: 'awake',
    mul: (c) => {
      if (c.hunger < 40) c.mul(c.self.cell, 2);
    },
  }),
  defineSymbol({
    id: 'hourglass',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 0,
    gate: 'awake',
    land: (c) => c.addSpins(c.mods.hourglassSpins, 'hourglass', { key: 'hourglass', max: c.mods.hourglassMax }),
  }),
  defineSymbol({
    id: 'key',
    tags: ['classic'],
    rarity: 'R',
    baseValue: 0,
    gate: 'awake',
    post: (c) => {
      if (c.inPayingPattern) c.grantFreeCharm();
    },
  }),
  defineSymbol({
    id: 'lock',
    tags: ['classic'],
    rarity: 'U',
    baseValue: 1,
    gate: 'awake',
    land: (c) => c.freeHold(c.self.cell.reel),
  }),
  defineSymbol({
    id: 'egg',
    tags: ['cursed'],
    rarity: 'U',
    baseValue: 0,
    gate: 'awake',
    land: (c) => {
      if (c.self.inst.age >= B.EGG_HATCH) c.transformRandom(c.self.inst, { rarity: 'R' });
    },
  }),
  defineSymbol({
    id: 'seed',
    tags: ['fruit'],
    rarity: 'C',
    baseValue: 0,
    gate: 'awake',
    land: (c) => {
      if (c.self.inst.age >= B.SEED_GROW) c.transformRandom(c.self.inst, { tag: 'fruit' });
    },
  }),
  defineSymbol({
    id: 'ghost',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 0,
    gate: 'awake',
    copy: (c) => {
      const left = c.grid.at(c.self.cell.reel - 1, c.self.cell.row);
      if (left) c.set(c.self.cell, c.value(left.cell));
    },
  }),
];

// Bones (§1.3): Bone is a partial wild for Skull and Tooth patterns.
const bones: SymbolDef[] = [
  defineSymbol({ id: 'bone', tags: ['bone'], rarity: 'U', baseValue: 2, gate: 'awake', wildFor: ['skull', 'tooth'] }),
  defineSymbol({
    id: 'skull',
    tags: ['cursed', 'bone'],
    rarity: 'R',
    baseValue: 6,
    gate: 'awake',
    mul: (c) => {
      if (c.grid.all().some((v) => v.def.tags.includes('tooth'))) c.mul(c.self.cell, 2);
    },
  }),
];

// Vermin that live among the awake symbols (§1.3).
const vermin: SymbolDef[] = [
  defineSymbol({
    id: 'spider',
    tags: ['vermin'],
    rarity: 'U',
    baseValue: 2,
    gate: 'awake',
    guard: true,
    add: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) c.add(n.cell, 1);
    },
  }),
  defineSymbol({
    id: 'crow',
    tags: ['vermin'],
    rarity: 'U',
    baseValue: 2,
    gate: 'awake',
    // Copy phase: sees the shiny's final VALUE, takes it on top of its own, and keeps +1.
    copy: (c) => {
      const shiny = c.grid.neighbors(c.self.cell).filter((n) => SHINY.includes(n.def.id));
      if (!shiny.length) return;
      const best = Math.max(...shiny.map((n) => c.value(n.cell)));
      c.add(c.self.cell, best);
      c.growLater(c.self.inst.uid, 1);
    },
  }),
  defineSymbol({
    id: 'leech',
    tags: ['vermin'],
    rarity: 'U',
    baseValue: 1,
    gate: 'awake',
    land: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) {
        if (n.def.tags.includes('vermin') || n.def.baseValue + n.inst.bonusValue <= 0) continue;
        c.grow(n.inst, -1);
        c.grow(c.self.inst, 1);
      }
    },
  }),
];
const SHINY = ['coin', 'moneybag', 'pearl'];

// ── Rot family (§1.4, after G_ROT) ─────────────────────────────────────
const isRotten = (v: { def: SymbolDef }): boolean => v.def.tags.includes('rotten');

/** One Rotten variant per fruit; they share this factory (docs/05 `rotten_*`). */
const rotten = (from: string): SymbolDef =>
  defineSymbol({ id: `rotten_${from}`, tags: ['fruit', 'rotten'], rarity: null, baseValue: 0, gate: 'rot', inert: true, rottenFrom: from });

const rot: SymbolDef[] = [
  ...['cherry', 'lemon', 'plum', 'grape', 'melon'].map(rotten),
  defineSymbol({
    id: 'maggot',
    tags: ['vermin'],
    rarity: 'U',
    baseValue: 1,
    gate: 'rot',
    add: (c) => {
      const flies = c.grid.all().some((v) => isLike(v.def, 'fly'));
      const eaten = new Set<number>();
      for (const n of c.grid.neighbors(c.self.cell)) {
        if (!isRotten(n) || eaten.has(n.inst.uid) || !c.destroyLater(n.inst.uid, 'maggot')) continue;
        eaten.add(n.inst.uid);
        c.growLater(c.self.inst.uid, B.MAGGOT_MEAL);
        if (flies) c.spawnLater('fly', B.FLY_SPAWN_CHANCE, 'fly');
      }
    },
  }),
  defineSymbol({
    id: 'fly',
    tags: ['vermin'],
    rarity: 'C',
    baseValue: 1,
    gate: 'rot',
    add: (c) => {
      const n = c.grid.all().filter(isRotten).length;
      if (n) c.add(c.self.cell, n);
    },
  }),
  defineSymbol({
    id: 'mushroom',
    tags: ['vermin'],
    rarity: 'U',
    baseValue: 2,
    gate: 'rot',
    land: (c) => {
      for (const n of c.grid.neighbors(c.self.cell)) if (isRotten(n)) c.transform(n.inst, 'mushroom');
    },
  }),
  defineSymbol({
    id: 'rat',
    tags: ['vermin'],
    rarity: 'C',
    baseValue: 1,
    gate: 'rot',
    add: (c) => {
      const others = c.grid.all().filter((v) => v !== c.self && isLike(v.def, 'rat')).length;
      if (others) c.add(c.self.cell, others);
    },
  }),
  defineSymbol({
    id: 'cat',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 3,
    gate: 'rot',
    add: (c) => {
      const eaten = new Set<number>();
      for (const n of c.grid.within(c.self.cell, c.mods.catReach)) {
        if (!isLike(n.def, 'rat') || eaten.has(n.inst.uid) || !c.destroyLater(n.inst.uid, 'cat')) continue;
        eaten.add(n.inst.uid);
        c.growLater(c.self.inst.uid, B.CAT_MEAL);
      }
    },
  }),
  defineSymbol({
    id: 'worm',
    tags: ['vermin'],
    rarity: 'C',
    baseValue: 1,
    gate: 'rot',
    land: (c) => {
      if (c.self.inst.age >= B.WORM_TURN) c.transform(c.self.inst, 'moth');
    },
  }),
];

// ── Hex (§1.5, after G_HEX) ────────────────────────────────────────────
const hex: SymbolDef[] = [
  defineSymbol({
    id: 'hex',
    tags: ['hex', 'cursed'],
    rarity: 'U',
    baseValue: B.HEX_VALUE,
    gate: 'hex',
    cleanse: true,
    land: (c) => c.applyHex('hex'),
  }),
  defineSymbol({ id: 'salt', tags: ['classic'], rarity: 'U', baseValue: 1, gate: 'hex', land: (c) => c.removeHex('salt') }),
];

// ── Eyes and Doom (§1.6, after G_EYES) ──────────────────────────────────
const eyes: SymbolDef[] = [
  defineSymbol({
    id: 'eye',
    tags: ['eye', 'cursed'],
    rarity: 'U',
    baseValue: 0,
    gate: 'eyes',
    // VALUE = 2 × the number of Eyes visible (Glass Eye: they count double here).
    add: (c) => c.add(c.self.cell, 2 * c.grid.all().filter((v) => v.def.tags.includes('eye')).length * c.mods.eyeCountX),
  }),
  defineSymbol({ id: 'tooth', tags: ['tooth', 'cursed'], rarity: 'U', baseValue: 4, gate: 'eyes' }),
  defineSymbol({ id: 'goldtooth', tags: ['tooth'], rarity: 'L', baseValue: 25, gate: 'eyes', goldenDevour: true }),
  defineSymbol({ id: 'halo', tags: ['light'], rarity: 'L', baseValue: 10, gate: 'eyes', blocksDevour: true, land: (c) => c.addDoom(-1) }),
  defineSymbol({
    id: 'moon',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 6,
    gate: 'eyes',
    mul: (c) => {
      if (c.doom >= B.MOON_DOOM) c.mul(c.self.cell, 2);
    },
  }),
  defineSymbol({
    id: 'mirror',
    tags: ['cursed'],
    rarity: 'R',
    baseValue: 0,
    gate: 'eyes',
    // Becomes a copy of the symbol on the opposite side of the grid (mirrored left↔right).
    land: (c) => {
      const other = c.grid.at(c.grid.reels - 1 - c.self.cell.reel, c.self.cell.row);
      if (other && other.def.id !== 'mirror') c.transform(c.self.inst, other.def.id);
    },
  }),
];

// ── Sigils (§1.7) ──────────────────────────────────────────────────────
const sigils: SymbolDef[] = [
  defineSymbol({ id: 'sig_scratch', tags: ['sigil'], rarity: 'U', baseValue: 0, gate: 'scratch', sigil: 'scratch' }),
  defineSymbol({ id: 'sig_ladder', tags: ['sigil'], rarity: 'U', baseValue: 0, gate: 'hilo', sigil: 'hilo' }),
  defineSymbol({ id: 'sig_wheel', tags: ['sigil'], rarity: 'R', baseValue: 0, gate: 'wheel', sigil: 'wheel' }),
  defineSymbol({ id: 'sig_teeth', tags: ['sigil'], rarity: 'R', baseValue: 0, gate: 'plinko', sigil: 'plinko' }),
  defineSymbol({ id: 'sig_feast', tags: ['sigil'], rarity: 'R', baseValue: 0, gate: 'feast', sigil: 'feast' }),
];

export const SYMBOL_LIST: SymbolDef[] = [...classic, ...awake, ...bones, ...vermin, ...rot, ...hex, ...eyes, ...sigils];
